import React from 'react';
import { Reveal, Stagger, StaggerItem } from './Reveal';

const processSteps = [
  {
    step: 1,
    title: "Initial Alignment Conversation",
    description: "A brief call to understand your goals, lifestyle, and concerns and which programme is right for you."
  },
  {
    step: 2,
    title: "Programme Information Pack",
    description: "You receive a structured document that outlines the programme framework, inclusions, support approach, and expectations around engagement."
  },
  {
    step: 3,
    title: "Comprehensive Assessment",
    description: "Once you confirm, an assessment form is shared to capture dietary habits, lifestyle patterns, medical history, and personal constraints."
  },
  {
    step: 4,
    title: "Diagnostic Review",
    description: "You are advised on relevant blood tests to assess internal markers that influence health and nutrition outcomes."
  },
  {
    step: 5,
    title: "Deep-Dive Consultation",
    description: "Blood reports and body composition are reviewed, lifestyle patterns are discussed in depth, and initial recommendations are shared based on your current health."
  },
  {
    step: 6,
    title: "Onboarding & Guideline Kit",
    description: "You receive a preparation guide covering essential groceries, supplements (if needed), and practical readiness before starting."
  },
  {
    step: 7,
    title: "Personalised Nutrition Plan & Walkthrough call",
    description: "A fully customised nutrition plan aligned to your health data, lifestyle, and goals is shared. A dedicated call to walk through the plan, clarify doubts, and align on execution."
  },
  {
    step: 8,
    title: "Daily Guidance & Real-Time Feedback",
    description: "You share meal updates and receive feedback on portions, food combinations, and practical adjustments, enabling real-time guidance, better adherence, and timely course correction."
  },
  {
    step: 9,
    title: "Progress-led Plan Refinement",
    description: "Regular weekly or fortnightly check-ins assess challenges and progress, with necessary adjustments and additional support such as travel or eating-out guidance."
  },
  {
    step: 10,
    title: "Goal Achievement & Long-Term Confidence",
    description: "By the end of the programme, clients gain a deeper understanding of their body, make confident food choices, and follow sustainable systems, leaving them empowered."
  }
];

export const ProcessSection = () => {
  return (
    <section 
      id="process"
      className="section-padding"
      style={{ background: 'var(--brand-lavender-mist)' }}
    >
      <div className="container-custom">
        <Reveal className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="heading-1 text-balance">Our 10 Vital Steps to Your Arogya</h2>
          <p className="body-large mt-6" style={{ color: 'var(--brand-muted-grey)' }}>
            A structured, evidence-based journey to lasting transformation
          </p>
        </Reveal>

        <div className="relative max-w-3xl mx-auto" data-testid="process-journey">
          {/* Vertical journey spine */}
          <div
            className="absolute top-0 bottom-0"
            style={{
              left: '27px',
              width: '2px',
              background: 'linear-gradient(to bottom, var(--brand-periwinkle), var(--brand-sage-green))',
              opacity: 0.5
            }}
          />

          <Stagger className="flex flex-col gap-8" stagger={0.12}>
            {processSteps.map((step, idx) => (
              <StaggerItem
                key={step.step}
                className="relative flex items-start gap-6"
                data-testid={`process-step-${step.step}`}
              >
                {/* Step node on the spine */}
                <div className="relative flex-shrink-0" style={{ zIndex: 1 }}>
                  <div
                    className="w-14 h-14 flex items-center justify-center font-bold text-white text-lg"
                    style={{
                      background: idx % 2 === 0 ? 'var(--brand-periwinkle)' : 'var(--brand-sage-green)',
                      borderRadius: '50%',
                      boxShadow: '0 4px 14px rgba(75, 46, 93, 0.18)',
                      border: '4px solid white'
                    }}
                  >
                    {step.step}
                  </div>
                </div>

                {/* Step content */}
                <div
                  className="flex-1 pb-2 transition-smooth glass-panel"
                  style={{
                    borderRadius: '18px',
                    padding: '24px 28px'
                  }}
                >
                  <p
                    className="text-xs font-semibold tracking-wider mb-2"
                    style={{ color: 'var(--brand-periwinkle)' }}
                  >
                    STEP {step.step}
                  </p>
                  <h3 className="heading-3 mb-3">{step.title}</h3>
                  <p className="body-regular">{step.description}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
};
